import { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { StyleSheet } from "react-native";

import { useClientApi, useContentResource, useTracker } from "@ctv/core";
import useUpload from "@ctv/core/api/useUpload";

import {
  Button,
  Card,
  DocumentField,
  DocumentFieldValue,
  InfoBox,
  Modal,
  Text,
  Token,
  View,
} from "@ctv/shared-core/src";

import { useDispatchSnackbar } from "@ctv/shared/contexts/SnackbarContext";

import { usePreRegisterConfirm } from "../_contexts/PreRegisterConfirmContext";
import { useRegisterContent } from "../_contexts/RegisterContentContext";
import {
  DOWNLOAD_DOCUMENT_API,
  DownloadDocumentRequest,
  DownloadDocumentResponse,
  SUBMIT_NDA_API,
  SubmitNDARequest,
  SubmitNDAResponse,
  UPLOAD_NDA_API,
  UploadNDARequest,
  UploadNDAResponse,
} from "../apis";

type Props = {
  isVisible: boolean;
  onSubmit(): void;
  onClose(): void;
};

type NDAFormValue = { nda: DocumentFieldValue };

export default function RequestNDAModal({
  isVisible,
  onSubmit,
  onClose,
}: Props) {
  const track = useTracker("ors-page");
  const openSnackbar = useDispatchSnackbar();

  const { token } = useRegisterContent();
  const cr = useContentResource().CorporateRegistration;
  const { corporateName, picEmail, country } = usePreRegisterConfirm()!;

  /**
   * useAPI
   */
  const upload = useUpload<UploadNDAResponse, UploadNDARequest>({
    path: UPLOAD_NDA_API,
  });

  const download = useClientApi<
    DownloadDocumentResponse,
    DownloadDocumentRequest
  >({
    domain: "management",
    method: "post",
    path: DOWNLOAD_DOCUMENT_API,
  });

  const submit = useClientApi<SubmitNDAResponse, SubmitNDARequest>({
    domain: "management",
    method: "post",
    path: SUBMIT_NDA_API,
  });

  /**
   * NDA Form Value
   */
  const methods = useForm<NDAFormValue>();
  const {
    handleSubmit: formSubmit,
    formState: { isSubmitSuccessful, errors },
  } = methods;

  async function handleUpload(_documentType: string, file: File) {
    try {
      const res = await upload(
        {
          country: country ?? "",
          token,
        },
        file
      );
      if (res.success) return res.data;
      else throw res.error;
    } catch (error) {
      /**
       * @TODO (Corporate Registration)
       * Need to add error message to content resource
       */
      openSnackbar({
        message: "Error while uploading NDA File",
        variant: "alert",
      });
    }
  }

  async function handleDownloadNDA() {
    download({
      documentType: "NDA",
      token,
    })
      .then((res) => {
        if (res.success) {
          window.open(res.data.url, "_blank")?.focus();
          track("corpB2b.onlineRegistration.webEvent", {
            eventName: "CORP_B2B.ONLINE_REGISTRATION.WEB_EVENT",
            eventVersion: "1.0.0",
            pageName: "ORS_PAGE",
            pageCategory: "ORS_PAGE",
            eventValue: "CLICK_DOWNLOAD_NDA",
            emailAddress: picEmail,
            corporateName,
          }).send();
        } else {
          openSnackbar({
            message: cr.failedDownloadErrorMessage,
            variant: "alert",
          });
        }
      })
      .catch(() =>
        openSnackbar({
          message: cr.failedDownloadErrorMessage,
          variant: "alert",
        })
      );
  }

  async function handleSubmit(values: NDAFormValue) {
    if (values.nda) {
      try {
        const response = await submit({
          fileName: values.nda.url,
          token,
        });

        if (response.success && response.data.status === "OK") {
          track("corpB2b.onlineRegistration.webEvent", {
            eventName: "CORP_B2B.ONLINE_REGISTRATION.WEB_EVENT",
            eventVersion: "1.0.0",
            pageName: "ORS_PAGE",
            pageCategory: "ORS_PAGE",
            eventValue: "SUBMIT_NDA",
            emailAddress: picEmail,
            corporateName,
          }).send();
        } else {
          throw new Error();
        }
      } catch (error) {
        openSnackbar({
          message: cr.failedRequestNdaErrorMessage,
          variant: "alert",
        });
      }
    }
  }

  return (
    <Fragment>
      <Modal
        visible={isVisible}
        onClose={onClose}
        render={() => (
          <Card style={[styles.container, { width: 600 }]}>
            <Text style={styles.spacingM} variant="headline">
              {cr.requestNdaTitle}
            </Text>
            <InfoBox
              variant="info"
              style={styles.spacingM}
              title={cr.requestNdaInfoTitle}
              Action={
                <Button
                  size="small"
                  variant="text-primary"
                  style={styles.infoBoxAction}
                  text={cr.requestNdaInfoAction}
                  onPress={handleDownloadNDA}
                />
              }
            >
              <Text variant="ui-small" ink="highlight">
                {cr.requestNdaInfoContent}
              </Text>
            </InfoBox>
            <Text style={styles.spacingM} ink="black-secondary">
              {cr.requestNdaUploadDescription}
            </Text>
            <View style={styles.spacingM}>
              <FormProvider {...methods}>
                <DocumentField
                  name="nda"
                  document={{
                    docType: "NDA",
                    extensions: [".pdf", ".png", ".jpeg"],
                    label: "",
                    mandatory: true,
                    maxSizeInMB: "20",
                    placeholder: cr.requestNdaUploadPlaceholder,
                    checkbox: null,
                    multiple: false,
                    subLabel: null,
                    tooltip: null,
                  }}
                  onChange={handleUpload}
                  documentExtensionCr={cr.documentExtension}
                  documentFileSizeCr={cr.documentFileSize}
                  documentRequiredCr={cr.documentRequired}
                />
              </FormProvider>
            </View>
            <View row justify="end">
              <Button
                variant="text-primary"
                text={cr.requestNdaCancelButton}
                onPress={onClose}
              />
              <Button
                style={styles.submitButton}
                variant="main-cta"
                text={cr.requestNdaSubmitButton}
                onPress={formSubmit(handleSubmit)}
              />
            </View>
          </Card>
        )}
      />
      <Modal
        visible={isSubmitSuccessful}
        render={() => (
          <Card style={[styles.container, { width: 320 }]}>
            <Text variant="title-1">{cr.submittedNdaTitle}</Text>
            <Text style={styles.submittedContent} ink="black-secondary">
              {cr.submittedNdaContent}
            </Text>
            <Button text={cr.submittedNdaOkButton} onPress={onSubmit} />
          </Card>
        )}
      />
    </Fragment>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: -Token.spacing.xl,
    padding: Token.spacing.m,
  },
  spacingM: {
    marginBottom: Token.spacing.m,
  },
  submitButton: {
    paddingHorizontal: Token.spacing.s,
    paddingVertical: Token.spacing.xs,
    marginLeft: Token.spacing.xl,
  },
  infoBoxAction: {
    paddingVertical: Token.spacing.xs,
  },
  infoBoxActionText: {
    fontSize: Token.typography.uiSmall.fontSize,
  },
  submittedContent: {
    marginTop: Token.spacing.m,
    marginBottom: Token.spacing.xs,
  },
});
